import { Raycaster, Vector2, Vector3 } from 'three';
import type { Object3D } from 'three';
import {
    isSketchArcObject,
    isSketchCollectionObject,
    isSketchContour,
    isSketchLineObject,
    isSketchEllipseObject,
    isSketchSlotObject,
    SketchPointKind,
    SketchPointObject,
} from '../../../mesh';
import type { SketchSlotObject } from '../../../mesh';
import { PointsMaterialFactory } from '../../../material';
import type { SketchRelationDefinition } from '../../solver/constraint';
import { SketchTrimConstraintCleanupService } from '../SketchTrimConstraintCleanupService';
import { SketchTrimSlotTopologyService } from '../SketchTrimSlotTopologyService';
import { SketchTrimCurveKind } from '../SketchTrimTypes';
import type {
    SketchTrimArcSnapshot,
    SketchTrimLineSnapshot,
    SketchTrimEllipseSnapshot,
    SketchTrimEntityRef,
} from '../SketchTrimTypes';
import type { SketchTrimDimensionStateSnapshot } from '../../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';
import type {
    SketchTrimPowerExtendPort,
    SketchTrimPowerExtendUndo,
} from '../../../../types/sketch/trim/SketchTrimPowerExtendTypes';
import { SketchTrimPowerExtendGeometry } from './SketchTrimPowerExtendGeometry';

/**
 * 一次 Shift 自然延伸手势。
 * 负责锁定目标、写入端点拓扑及恢复命令内状态；模型历史由 Power 模式统一提交。
 */
export class SketchTrimPowerExtendService implements SketchTrimPowerExtendUndo {
    /** 延伸所依赖的 SDK 端口。 */
    private readonly port: SketchTrimPowerExtendPort;
    /** 自然路径计算器。 */
    private readonly geometry = new SketchTrimPowerExtendGeometry();
    /** 屏幕投影射线缓存。 */
    private readonly raycaster = new Raycaster();
    /** 屏幕归一化坐标缓存。 */
    private readonly ndc = new Vector2();
    /** 鼠标参考面投影缓存。 */
    private readonly pointer = new Vector3();
    /** 初始几何及端点身份。 */
    private before: SketchTrimLineSnapshot | SketchTrimArcSnapshot | SketchTrimEllipseSnapshot | null = null;
    /** 起点原父对象，用于恢复圆弧子树中的共享点所有权。 */
    private startParentBefore: Object3D | null = null;
    /** 终点原父对象。 */
    private endParentBefore: Object3D | null = null;
    /** 初始尺寸仓库。 */
    private dimensionsBefore: SketchTrimDimensionStateSnapshot | null = null;
    /** 待恢复的初始关系定义。 */
    private readonly relationsBefore = new Map<number, SketchRelationDefinition>();
    /** 延伸创建的独立端点，撤销时解除挂载。 */
    private readonly createdPoints: SketchPointObject[] = [];
    /** 槽口降级前容器。 */
    private sourceSlot: SketchSlotObject | null = null;
    /** 槽口降级前父对象。 */
    private slotParent: Object3D | null = null;
    /** 槽口降级前子对象。 */
    private slotChildren: Object3D[] = [];
    /** 是否已经产生实际几何变更。 */
    public changed = false;
    /** 最初命中引用，供红方块使用。 */
    public target: SketchTrimEntityRef | null = null;

    /** @param port 已初始化的 SDK 命令依赖。 */
    public constructor(port: SketchTrimPowerExtendPort) {
        this.port = port;
    }

    /**
     * 锁定鼠标下可自然延伸的实体。
     * @param clientX 客户端 X。
     * @param clientY 客户端 Y。
     * @returns 是否锁定直线、中心线、开放圆弧或部分椭圆。
     */
    public begin(clientX: number, clientY: number): boolean {
        const picked = this.port.servicePickTrimCurveAtClientPoint(
            clientX,
            clientY,
            this.port.serviceGetOptions().ignoreConstructionGeometry,
        );
        if (!picked) return false;
        const curve = this.port.serviceResolvePowerTrimTarget(picked);
        if (
            !isSketchLineObject(curve) &&
            !isSketchArcObject(curve) &&
            !(isSketchEllipseObject(curve) && curve.isPartialEllipse())
        )
            return false;
        const snapshot = this.port.serviceCaptureCurveSnapshot(curve);
        if (
            !snapshot ||
            (snapshot.curveKind !== SketchTrimCurveKind.Line &&
                snapshot.curveKind !== SketchTrimCurveKind.Arc &&
                snapshot.curveKind !== SketchTrimCurveKind.Ellipse)
        )
            return false;
        this.before = snapshot;
        this.startParentBefore = snapshot.startPoint.parent;
        this.endParentBefore = snapshot.endPoint.parent;
        if (!this.projectPointer(clientX, clientY)) {
            this.before = null;
            return false;
        }
        if (snapshot.curveKind === SketchTrimCurveKind.Line) {
            this.geometry.beginLine(
                new Vector3(...snapshot.startPosition),
                new Vector3(...snapshot.endPosition),
                this.pointer,
            );
        } else if (snapshot.curveKind === SketchTrimCurveKind.Arc) {
            this.geometry.beginArc(
                snapshot.definition,
                curve.referencePlane.xAxis,
                curve.referencePlane.yAxis,
                this.pointer,
            );
        } else {
            this.geometry.beginEllipse(
                snapshot.definition,
                curve.referencePlane.xAxis,
                curve.referencePlane.yAxis,
                this.pointer,
            );
        }
        this.target = {
            objectId: picked.objectId,
            segmentIndex: picked.segmentIndex,
            hitPoint: picked.hitPoint ?? [this.pointer.x, this.pointer.y, this.pointer.z],
        };
        this.dimensionsBefore = this.port.serviceCaptureActiveSketchDimensionState();
        // 只捕获定义引用，不删除关系；没有实际拖动的点击不能改变约束或尺寸。
        for (const object of [curve, ...curve.getRelatedSketchPointsSnapshot()]) {
            for (const relation of object.constraints.getRelations()) this.relationsBefore.set(relation.id, relation);
        }
        if (isSketchSlotObject(curve.parent)) {
            this.sourceSlot = curve.parent;
            this.slotParent = curve.parent.parent;
            // 轮廓在降级时被销毁，撤销应重新派生，不能重新挂载旧 GPU 资源。
            this.slotChildren = curve.parent.children.filter((child) => !isSketchContour(child));
        }
        return true;
    }

    /**
     * 沿已锁定目标的自然路径延伸。
     * @param clientX 当前客户端 X。
     * @param clientY 当前客户端 Y。
     * @returns 本帧是否写入了新几何。
     */
    public update(clientX: number, clientY: number): boolean {
        const before = this.before;
        if (!before || !this.projectPointer(clientX, clientY)) return false;
        const result = this.geometry.update(this.pointer);
        const curve = before.line;
        const start =
            before.curveKind === SketchTrimCurveKind.Line ? before.line.startPoint : before.line.getStartPoint();
        const end = before.curveKind === SketchTrimCurveKind.Line ? before.line.endPoint : before.line.getEndPoint();
        const startChanged = start.getPosition().distanceToSquared(result.start) > 1e-20;
        const endChanged = end.getPosition().distanceToSquared(result.end) > 1e-20;
        if (!startChanged && !endChanged) return false;

        try {
            if (!this.changed) {
                // 与裁剪共用关系清理及槽口降级规则，维持 SDK 的统一拓扑生命周期。
                new SketchTrimConstraintCleanupService().cleanupCurveConstraints(curve);
                const slotChanges = new SketchTrimSlotTopologyService(this.port.getViewer()).dissolveSlotForTrimTarget(
                    curve,
                );
                this.port.serviceApplyDimensionTopologyChanges(slotChanges.topologyChanges);
            }
            if (startChanged) this.detachSharedEndpoint('start');
            if (endChanged) this.detachSharedEndpoint('end');
            if (before.curveKind === SketchTrimCurveKind.Line) {
                // 静默写端点后统一刷新，禁止对象式求解覆盖同一手势的候选几何。
                before.line.startPoint.syncPositionSilently(result.start);
                before.line.endPoint.syncPositionSilently(result.end);
                before.line.updateGeometry();
            } else if (before.curveKind === SketchTrimCurveKind.Arc) {
                before.line.updateArcDefinition({
                    ...before.definition,
                    startAngle: result.startAngle,
                    endAngle: result.endAngle,
                });
            } else {
                before.line.updateEllipseDefinition({
                    ...before.definition,
                    startAngle: result.startAngle,
                    endAngle: result.endAngle,
                });
            }
            this.port.serviceApplyDimensionTopologyChanges([
                {
                    sourceEntityId: curve.persistentId,
                    targetEntityId: curve.persistentId,
                    pointMappings: [
                        {
                            sourcePointId: before.startPoint.persistentId,
                            targetPointId: (before.curveKind === SketchTrimCurveKind.Line
                                ? before.line.startPoint
                                : before.line.getStartPoint()
                            ).persistentId,
                            targetEntityId: curve.persistentId,
                        },
                        {
                            sourcePointId: before.endPoint.persistentId,
                            targetPointId: (before.curveKind === SketchTrimCurveKind.Line
                                ? before.line.endPoint
                                : before.line.getEndPoint()
                            ).persistentId,
                            targetEntityId: curve.persistentId,
                        },
                    ],
                    deletedPointIds: [],
                    invalidatedDimensionKinds: before.curveKind === SketchTrimCurveKind.Line ? ['length'] : [],
                },
            ]);
            if (!this.port.getViewer().solveActiveSketchParametricProperties(curve)) {
                // 外层拖拽会话负责恢复整组 solver 联动状态，不能留下半完成延伸。
                throw new Error(`强力延伸后的草图约束无法满足，实体=${curve.persistentId}。`);
            }
            this.changed = true;
            this.refreshContour();
            this.port.getViewer().requestRender();
            return true;
        } catch (error) {
            // SDK 无活动模型历史时仍恢复本手势；外层事务再负责整组联动状态的补偿。
            this.undo();
            throw error;
        }
    }

    /**
     * 在原位置分离仍被其它实体使用的端点，避免延伸牵动未选实体。
     * @param side 要移动的一侧。
     */
    private detachSharedEndpoint(side: 'start' | 'end'): void {
        const before = this.before!;
        const curve = before.line;
        const point =
            before.curveKind === SketchTrimCurveKind.Line
                ? side === 'start'
                    ? before.line.startPoint
                    : before.line.endPoint
                : side === 'start'
                  ? before.line.getStartPoint()
                  : before.line.getEndPoint();
        if (!point.getConnectedEdgesSnapshot().some((edge) => edge !== curve)) return;
        const replacement = new SketchPointObject({
            position: point.getPosition(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: curve.referencePlane,
        });
        this.createdPoints.push(replacement);
        if (before.curveKind === SketchTrimCurveKind.Arc) {
            const definition = before.line.getArcDefinition();
            if (side === 'start') before.line.setStartPoint(replacement, definition);
            else before.line.setEndPoint(replacement, definition);
        } else if (side === 'start') before.line.setStartPoint(replacement);
        else before.line.setEndPoint(replacement);
        if (curve.parent) this.port.getViewer().addObject(replacement, curve.parent);
        if (point.parent === curve && curve.parent) this.port.getViewer().addObject(point, curve.parent);
        this.port.getViewer().interactionStateManager.syncExternalMaterialSnapshot(replacement);
    }

    /**
     * 使用复用的射线和向量投影鼠标，不在高频路径创建 Three 数学对象。
     * @param clientX 客户端 X。
     * @param clientY 客户端 Y。
     * @returns 投影是否有效。
     */
    private projectPointer(clientX: number, clientY: number): boolean {
        const viewer = this.port.getViewer();
        const rect = viewer.container?.getBoundingClientRect();
        if (!viewer.camera || !rect || rect.width <= 0 || rect.height <= 0 || !this.before) return false;
        this.ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, (-(clientY - rect.top) / rect.height) * 2 + 1);
        this.raycaster.setFromCamera(this.ndc, viewer.camera);
        return this.raycaster.ray.intersectPlane(this.before.line.referencePlane, this.pointer) !== null;
    }

    /** 重新判断被修改集合的闭合状态，及时去掉已打开轮廓的上色。 */
    private refreshContour(): void {
        const parent = this.before?.line.parent;
        if (!isSketchCollectionObject(parent)) return;
        if (parent.isClosedGeometry && this.port.getViewer().shadedSketchContoursEnabled) parent.syncContour();
        else parent.clearContourDisplay();
    }

    /** 恢复当前手势前的端点身份、实体定义、关系、尺寸及槽口容器。 */
    public undo(): void {
        const before = this.before;
        if (!before) return;
        // 先改回原端点身份，再恢复坐标，避免旧圆弧定义写到新端点上。
        if (before.curveKind === SketchTrimCurveKind.Arc) {
            before.line.setStartPoint(before.startPoint, before.definition);
            before.line.setEndPoint(before.endPoint, before.definition);
        } else if (before.curveKind === SketchTrimCurveKind.Ellipse) {
            // 椭圆端点 setter 仅迁移身份，随后由快照定义统一恢复角度、半轴和派生控制点。
            before.line.setStartPoint(before.startPoint);
            before.line.setEndPoint(before.endPoint);
        }
        this.port.serviceRestoreCurveSnapshot(before);
        this.startParentBefore?.add(before.startPoint);
        this.endParentBefore?.add(before.endPoint);
        for (const point of this.createdPoints) {
            point.removeFromParent();
            // Sprite 使用工厂共享材质，不在这里销毁共享 GPU 资源。
            point.clear();
        }
        for (const relation of this.relationsBefore.values()) {
            for (const target of relation.getTargetsSnapshot()) target.constraints.addRelation(relation);
        }
        if (this.sourceSlot && this.slotParent) {
            this.slotParent.add(this.sourceSlot);
            for (const child of this.slotChildren) this.sourceSlot.add(child);
            for (const child of this.slotChildren) {
                if (isSketchLineObject(child) || isSketchArcObject(child))
                    child.attachClosedCollection(this.sourceSlot);
            }
            if (this.port.getViewer().shadedSketchContoursEnabled) this.sourceSlot.syncContour();
        }
        this.port.serviceRestoreSketchDimensionState(this.dimensionsBefore);
        this.refreshContour();
        this.port.getViewer().requestRender();
        this.release();
    }

    /** 释放命令记录引用；正式点对象由草图及历史管理器持有并负责生命周期。 */
    public release(): void {
        this.before = null;
        this.startParentBefore = null;
        this.endParentBefore = null;
        this.dimensionsBefore = null;
        this.relationsBefore.clear();
        this.createdPoints.length = 0;
        this.sourceSlot = null;
        this.slotParent = null;
        this.slotChildren.length = 0;
        this.target = null;
    }
}
